import moment from 'moment';
import { HdBodygraphChartPreset, HdBodygraphPlanetaryActivation } from '../bodygraph/hd-bodygraph.models';

/**
 * Een tab van een opgeslagen chart bewaart een volledige kopie van het document (localStorage én
 * de server-app-status, dus over herstarts en toestellen heen). De bibliotheek is de enige bron
 * van waarheid; deze helpers vergelijken zo'n tabkopie met het bibliotheekdocument.
 *
 * Aanleiding (2026-10-22): een chart toonde in zijn tab 14:22 terwijl het bibliotheekdocument
 * (en dus "Chart bewerken") al 12:55 had; de tab werd na het overschrijven nooit ververst.
 */

interface StoredBirthTime {
  utc?: string | null;
  local?: string | null;
  timezone?: { id?: string | null } | null;
}

interface StoredBirthData {
  time?: StoredBirthTime | null;
  location?: {
    city?: string | null;
    country?: string | null;
    lat?: number | null;
    lon?: number | null;
  } | null;
}

function birthOf(birthData: unknown): StoredBirthData {
  return (birthData ?? {}) as StoredBirthData;
}

/** UTC-instant op de minuut; tolerant voor notatieverschillen ("Z" of "+00:00", met of zonder seconden). */
function instantKey(iso: string | null | undefined): string {
  if (!iso) return '';
  const parsed = moment.utc(iso, moment.ISO_8601);
  return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm') : iso;
}

/** Kloktijd op de geboorteplaats, letterlijk tot op de minuut (bewust zonder tijdzone-omrekening). */
function wallClockKey(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 16) : '';
}

function activationTokens(activations: readonly HdBodygraphPlanetaryActivation[] | null | undefined): string {
  return (activations ?? [])
    .map(activation => `${activation.side}:${activation.planetId}:${activation.gate}.${activation.line}`)
    .sort()
    .join(',');
}

function birthFingerprint(birthData: unknown): string {
  const birth = birthOf(birthData);
  const location = birth.location ?? {};
  return [
    instantKey(birth.time?.utc),
    wallClockKey(birth.time?.local),
    birth.time?.timezone?.id ?? '',
    location.city ?? '',
    location.country ?? '',
    location.lat ?? '',
    location.lon ?? '',
  ].join('|');
}

/**
 * Het geboortemoment waarop een chart berekend is: het UTC-moment van de geboorte, bij een cycle-
 * of momentchart aangevuld met het cyclus-/momenttijdstip. Twee documenten met een verschillende
 * sleutel tonen een andere chart; een save mag het ene dan nooit stil door het andere vervangen.
 */
export function hdChartBirthMomentKey(preset: HdBodygraphChartPreset): string {
  return [
    instantKey(birthOf(preset.birthData).time?.utc),
    instantKey(preset.cycle?.anchorUtc),
    instantKey(preset.transit?.momentUtc),
    instantKey(preset.transit?.momentEndUtc),
    instantKey(birthOf(preset.composite?.partnerChart?.birthData).time?.utc),
  ].join('|');
}

/**
 * Inhoudelijke vingerafdruk: naam, geboortemoment en -plaats, en de berekende activaties (ook die
 * van het cycle-moment, de momentchart en de composite-partner). Een clean tab van een opgeslagen
 * chart hoort dezelfde vingerafdruk te hebben als het bibliotheekdocument. De gestempelde
 * `propertiesSummary` en het handwerk (pins, tooltip) tellen bewust niet mee: de summary landt
 * alleen in het bewaarde document, en handwerk volgt al via de bestaande verversingen.
 */
export function hdChartContentFingerprint(preset: HdBodygraphChartPreset): string {
  return JSON.stringify([
    preset.name ?? '',
    birthFingerprint(preset.birthData),
    activationTokens(preset.planetaryActivations),
    instantKey(preset.cycle?.anchorUtc),
    activationTokens(preset.cycle?.moment?.planetaryActivations),
    instantKey(preset.transit?.momentUtc),
    instantKey(preset.transit?.momentEndUtc),
    activationTokens(preset.transit?.personalityActivations),
    preset.composite?.partner?.chartId ?? '',
    birthFingerprint(preset.composite?.partnerChart?.birthData),
    activationTokens(preset.composite?.partnerChart?.planetaryActivations),
  ]);
}

/**
 * Leesbaar geboortemoment voor meldingen: de kloktijd op de geboorteplaats ("22-10-1980 12:55"),
 * anders het UTC-moment met "UTC" erachter; leeg zonder bruikbare tijd.
 */
export function hdChartBirthMomentLabel(preset: HdBodygraphChartPreset): string {
  const time = birthOf(preset.birthData).time;
  const local = time?.local ? moment(wallClockKey(time.local), 'YYYY-MM-DDTHH:mm', true) : undefined;
  if (local?.isValid()) return local.format('DD-MM-YYYY HH:mm');
  const utc = time?.utc ? moment.utc(time.utc, moment.ISO_8601) : undefined;
  return utc?.isValid() ? `${utc.format('DD-MM-YYYY HH:mm')} UTC` : '';
}
